import { appPath, parseAddress } from '@featit/shared/addressing/paths';
import { barTitleOf } from '@featit/shared/bar-title';
import {
  chromeLabelText,
  recordLabel,
  viewLabel,
  type ChromeLabelType,
  type ChromeTranslateFn,
} from '@featit/shared/chrome-label';

/**
 * IU-3 (`docs/concept-iphone-ux.md`, S-4 *Every pushed route declares its parent route*): where a
 * pushed screen's back row goes, and what a tab root is.
 *
 * ⭐ **One table, read by the back row and by nothing else.** Back with an in-app history entry pops
 * it; back with none — a cold open from a push or a shared link — replaces the route with the parent
 * here, so mobile Safari never leaves FEATit and the app always has a list to return to.
 *
 * ⛔ Matched on the address's own segments (`parseAddress`), never on substrings: a slug may be called
 * `items` (I-0385).
 *
 * T-0838 (IN-29): the table names views by their `chromeTitles` keys and records by their own
 * names (`ChromeLabelType`); `routeParentOf` says them in the reader's language through `t`, and
 * the 12-character rule is measured on that text.
 */

/** The kind of record a pushed screen shows. */
export type PushedRecordType = 'item' | 'decision' | 'task' | 'usage' | 'part' | 'page';

export interface IRouteParent {
  /** The parent's address: the list the record belongs to, in the same scope. */
  readonly path: string;
  /** The parent view's name, as its tab says it (*Items*, *Decisions*). */
  readonly label: string;
  /** The record's key (*I-0016*, *D-0001*) — what the back row shows, never the view's name. */
  readonly recordKey: string;
  readonly recordType: PushedRecordType;
}

/** The table's answer before it is said in a language: what the parent and the record are called. */
interface IRouteParentSource {
  readonly path: string;
  readonly label: ChromeLabelType;
  readonly recordKey: ChromeLabelType;
  readonly recordType: PushedRecordType;
  /** A sub-page's parent label is held to S-4's length here; a record's where its row is drawn. */
  readonly measured?: boolean;
}

/** The five tab roots that draw the top row (S-4's table). */
export type PhoneTabRootType = 'decisions' | 'activity' | 'items' | 'roadmap' | 'agents';

/** The pushed views, by the segment that names their list, and what that list is called. */
const PUSHED_LISTS: Readonly<Record<string, { label: ChromeLabelType; type: PushedRecordType }>> = {
  items: { label: viewLabel('items'), type: 'item' },
  decisions: { label: viewLabel('decisions'), type: 'decision' },
  tasks: { label: viewLabel('tasks'), type: 'task' },
  // I-1101: a part's page (`parts/:handle`) is pushed from the Parts tree like any record.
  parts: { label: viewLabel('parts'), type: 'part' },
};

/** The routes under a list that are not a record: creating one. */
const NOT_A_RECORD = new Set(['new']);

/** S-4: *‹ and the parent view's name, at most 12 characters, else Back*. */
export const BACK_LABEL_MAX = 12;

/** The back control's label for `parent`, in the language `parent.label` is already said in. */
export function backLabelOf(parent: Pick<IRouteParent, 'label'>, t: ChromeTranslateFn): string {
  return parent.label.length <= BACK_LABEL_MAX ? parent.label : t('chromeTitles.back');
}

function segmentsOf(route: string): string[] {
  return route.split('/').filter(Boolean);
}

/** The pathname up to and including its `/-/`, for a work area's address. */
function workAreaBase(pathname: string): string {
  const separator = pathname.indexOf('/-/');
  return separator === -1 ? `${pathname.replace(/\/$/, '')}/-/` : pathname.slice(0, separator + 3);
}

/**
 * The parent of a pushed screen, or `null` for a screen that is not one (a tab root, Settings, a
 * page). Every pushed route the phone layout draws a back row on is a line here. `pageTitle` is the
 * screen's own name where only the page knows it (a view opened from Views, I-1366).
 */
export function routeParentOf(
  pathname: string,
  t: ChromeTranslateFn,
  pageTitle?: string | null,
): IRouteParent | null {
  const source = routeParentSourceOf(pathname, pageTitle);
  if (!source) return null;
  const label = chromeLabelText(source.label, t);
  return {
    path: source.path,
    label: source.measured && label.length > BACK_LABEL_MAX ? t('chromeTitles.back') : label,
    recordKey: chromeLabelText(source.recordKey, t),
    recordType: source.recordType,
  };
}

/** Whether the screen at `pathname` is pushed over a parent: what `routeParentOf` answers, unsaid. */
export function hasRouteParent(pathname: string): boolean {
  return routeParentSourceOf(pathname) !== null;
}

function routeParentSourceOf(
  pathname: string,
  pageTitle?: string | null,
): IRouteParentSource | null {
  return recordParentOf(pathname) ?? pushedPageParentOf(pathname, pageTitle);
}

/**
 * I-1101: the shell hides its own bar for EVERY phone screen once the web announces `drawsChrome`,
 * so every screen the web draws is one of four kinds, and the guard
 * (`pushedRoutesDrawBackRow.guard.test.ts`) turns a route that is none of them red:
 *
 * - `tab-root`: draws the top row (`phoneTabRootOf`).
 * - `drawer-root`: a destination opened from the More drawer or its web panel (Settings, the Parts
 *   tree, Tasks for me, a work area's home). A root: it draws the page top row, its title and a
 *   control that goes back to the drawer.
 * - `pushed`: a detail or sub-page. It declares its parent and draws the back row.
 * - `own-chrome`: draws its own (a sheet, the More panel itself).
 *
 * Named by `barTitleOf`, the one table of page names: a route it has no name for is `null`, which is
 * the fail-closed case — no row, and a red guard.
 */
export type PhonePageKindType = 'tab-root' | 'drawer-root' | 'pushed' | 'own-chrome';

/** Screens that draw their own chrome, by their address's route (`items/new` is IU-10's sheet). */
const OWN_CHROME_ROUTES = new Set(['more', 'items/new']);

/**
 * F-0601 AMX-6a to AMX-6f: the retired Infrastructure addresses, `/-/infrastructure` and
 * `/-/infrastructure/machines/:machineId/permissions`, only redirect to the Agents explorer and draw
 * nothing of their own, so they are own chrome: no row is drawn for the moment they stand.
 */
function isRetiredInfrastructureRoute(segments: readonly string[]): boolean {
  if (segments[0] !== 'infrastructure') return false;
  return (
    segments.length === 1 ||
    (segments.length === 4 && segments[1] === 'machines' && segments[3] === 'permissions')
  );
}
/** `/-/work-areas/<x>` and `/-/namespaces/:id/<x>`: lists the drawer opens, not details. */
const DRAWER_LISTS = new Set(['activity', 'views', 'documents', 'tasks', 'decisions']);

function pageSegments(
  pathname: string,
): { scope: 'app' | 'work-area' | 'namespace'; segments: string[] } | null {
  const address = parseAddress(pathname);
  if (address.kind === 'namespace') return { scope: 'namespace', segments: [] };
  if (address.kind !== 'work-area' && address.kind !== 'app') return null;
  return { scope: address.kind, segments: segmentsOf(address.route) };
}

function isDrawerRoot({ scope, segments }: NonNullable<ReturnType<typeof pageSegments>>): boolean {
  if (scope === 'namespace') return true;
  if (scope === 'work-area') return segments.length <= 1;
  const [first, second, third] = segments;
  if (segments.length <= 1) return true;
  if (first === 'work-areas') return segments.length === 2 && DRAWER_LISTS.has(second);
  if (first === 'namespaces') return segments.length === 2 || (segments.length === 3 && !!third);
  if (first === 'organisations') return segments.length === 2;
  return false;
}

export function phonePageKindOf(pathname: string): PhonePageKindType | null {
  if (phoneTabRootOf(pathname) !== null) return 'tab-root';
  const page = pageSegments(pathname);
  if (!page) return null;
  if (OWN_CHROME_ROUTES.has(page.segments.join('/'))) return 'own-chrome';
  if (page.scope === 'app' && isRetiredInfrastructureRoute(page.segments)) return 'own-chrome';
  if (recordParentOf(pathname) !== null) return 'pushed';
  if (barTitleOf(pathname).view === null) return null;
  return isDrawerRoot(page) ? 'drawer-root' : 'pushed';
}

/** A drawer-root screen's title, and where a cold open's Back lands. */
export function drawerRootOf(
  pathname: string,
  t: ChromeTranslateFn,
): { title: string; path: string } | null {
  if (phonePageKindOf(pathname) !== 'drawer-root') return null;
  const address = parseAddress(pathname);
  return {
    title: chromeLabelText(barTitleOf(pathname).label, t),
    path: address.kind === 'work-area' ? `${workAreaBase(pathname)}items` : appPath('/items'),
  };
}

/**
 * I-1955: a record whose list segment has no page of its own, and the drawer root that lists it
 * instead. A dashboard page (`pages/<id>`) is listed beside the views, so Back from a cold open
 * goes to Views, not to Items.
 */
const ROOTLESS_LISTS: Readonly<Record<string, string>> = { pages: 'views' };

/** A pushed sub-page's parent: the page one segment up, when that is a list the person can reach. */
function pushedPageParentOf(
  pathname: string,
  pageTitle?: string | null,
): IRouteParentSource | null {
  if (phonePageKindOf(pathname) !== 'pushed') return null;
  const page = pageSegments(pathname);
  if (!page) return null;
  // I-1366 (IL-18a): the row's title is the page's own name, never its list's ("‹ Views  Views").
  const title = barTitleOf(pathname, { pageTitle }).label;
  const up =
    ROOTLESS_LISTS[page.segments[0]] && page.segments.length === 2
      ? [ROOTLESS_LISTS[page.segments[0]]]
      : // F-0710 CRQ-12 (I-2492): a request opened on its own goes back to Shared with you; the
        // work area's segment between them is no page.
        page.scope === 'app' && page.segments[0] === 'shared'
        ? ['shared']
        : page.segments.slice(0, -1);
  const upPath =
    page.scope === 'work-area'
      ? `${workAreaBase(pathname)}${up.join('/')}`.replace(/\/$/, '')
      : appPath(`/${up.join('/')}`);
  const upKind = up.length > 0 ? phonePageKindOf(upPath) : null;
  const reachable = upKind === 'drawer-root' || upKind === 'tab-root';
  return {
    path: reachable
      ? upPath
      : page.scope === 'work-area'
        ? `${workAreaBase(pathname)}items`
        : appPath('/items'),
    label: reachable ? barTitleOf(upPath).label : viewLabel('back'),
    recordKey: title,
    recordType: 'page',
    measured: true,
  };
}

function recordParentOf(pathname: string): IRouteParentSource | null {
  const address = parseAddress(pathname);
  if (address.kind === 'work-area') {
    const [view, key, ...rest] = segmentsOf(address.route);
    const list = PUSHED_LISTS[view];
    // IU-12b (F-0540): an item pushed from the work area's own Activity goes back to it.
    if (view === 'activity' && key && rest.length === 0) {
      return {
        path: `${workAreaBase(pathname)}activity`,
        label: viewLabel('activity'),
        recordKey: recordLabel(decodeURIComponent(key)),
        recordType: 'item',
      };
    }
    // I-1955: a schedule's own sub-pages, one run (`schedules/<slug>/runs/<id>`, a page of its own
    // on a phone) and its form (`schedules/<slug>/edit`), are pushed over that schedule, so Back
    // returns to it, a cold open too: never to Items. The row's title is the bar's own.
    const scheduleSubPage =
      (rest.length === 2 && rest[0] === 'runs') || (rest.length === 1 && rest[0] === 'edit');
    if (view === 'schedules' && key && key !== 'new' && scheduleSubPage) {
      return {
        path: `${workAreaBase(pathname)}schedules/${key}`,
        label: viewLabel('schedule'),
        recordKey: barTitleOf(pathname).label,
        recordType: 'page',
      };
    }
    if (!list || !key || NOT_A_RECORD.has(key)) return null;
    // I-1101: Revise a decision is pushed over the decision, so back returns to it.
    if (view === 'decisions' && rest.length === 1 && rest[0] === 'revise') {
      return {
        path: `${workAreaBase(pathname)}decisions/${key}`,
        label: viewLabel('decision'),
        recordKey: viewLabel('revise'),
        recordType: 'decision',
      };
    }
    if (rest.length > 0) return null;
    return {
      path: `${workAreaBase(pathname)}${view}`,
      label: list.label,
      recordKey: recordLabel(decodeURIComponent(key)),
      recordType: list.type,
    };
  }
  if (address.kind !== 'app') return null;
  const segments = segmentsOf(address.route);
  // `/-/items/I-0001`, `/-/decisions/D-0001`, `/-/tasks/T-0001`: the selected work area's.
  if (segments.length === 2 && PUSHED_LISTS[segments[0]] && !NOT_A_RECORD.has(segments[1])) {
    const list = PUSHED_LISTS[segments[0]];
    return {
      path: appPath(`/${segments[0]}`),
      label: list.label,
      recordKey: recordLabel(decodeURIComponent(segments[1])),
      recordType: list.type,
    };
  }
  // T-0565: `/-/work-areas/activity/<work area>/I-0001`, an item pushed from All's Activity feed;
  // T-0566: `/-/namespaces/:id/activity/<work area>/I-0001`, from an organisation's.
  const activityScope =
    segments[0] === 'work-areas' && segments[1] === 'activity' && segments.length >= 5
      ? 'work-areas/activity'
      : segments[0] === 'namespaces' && segments[2] === 'activity' && segments.length >= 6
        ? `namespaces/${segments[1]}/activity`
        : null;
  if (activityScope) {
    return {
      path: appPath(`/${activityScope}`),
      label: viewLabel('activity'),
      recordKey: recordLabel(decodeURIComponent(segments[segments.length - 1])),
      recordType: 'item',
    };
  }
  // `/-/work-areas/decisions/<work area>/D-0001` (I-0602), and its tasks twin (I-0954).
  if (segments[0] === 'work-areas' && segments.length >= 5) {
    const list = PUSHED_LISTS[segments[1]];
    if (list && list.type !== 'item') {
      return {
        path: appPath(`/work-areas/${segments[1]}`),
        label: list.label,
        recordKey: recordLabel(decodeURIComponent(segments[segments.length - 1])),
        recordType: list.type,
      };
    }
  }
  // `/-/namespaces/:id/decisions/<work area>/D-0001` (F-0383, NV-6), and its tasks twin.
  if (segments[0] === 'namespaces' && segments.length >= 6) {
    const list = PUSHED_LISTS[segments[2]];
    if (list && list.type !== 'item') {
      return {
        path: appPath(`/namespaces/${segments[1]}/${segments[2]}`),
        label: list.label,
        recordKey: recordLabel(decodeURIComponent(segments[segments.length - 1])),
        recordType: list.type,
      };
    }
  }
  // Agents' Usage (the concept: *an agent and Usage, Agents*).
  if (segments.join('/') === 'agents/preview/usage') {
    return {
      path: appPath('/agents'),
      label: viewLabel('agents'),
      recordKey: viewLabel('usage'),
      recordType: 'usage',
    };
  }
  return null;
}

/** Which tab root a pathname is, or `null` for any other screen (S-4's table). */
export function phoneTabRootOf(pathname: string): PhoneTabRootType | null {
  const address = parseAddress(pathname);
  if (address.kind === 'work-area') {
    const segments = segmentsOf(address.route);
    if (segments.length !== 1) return null;
    return segments[0] === 'decisions' ||
      segments[0] === 'activity' ||
      segments[0] === 'items' ||
      segments[0] === 'roadmap'
      ? segments[0]
      : null;
  }
  if (address.kind !== 'app') return null;
  const route = segmentsOf(address.route).join('/');
  switch (route) {
    case 'decisions':
    case 'work-areas/decisions':
      return 'decisions';
    case 'activity':
      return 'activity';
    case 'items':
      return 'items';
    case 'roadmap':
      return 'roadmap';
    case 'agents':
      return 'agents';
    default:
      // A namespace's own Decisions (`/-/namespaces/:id/decisions`).
      return /^namespaces\/[^/]+\/decisions$/.test(route) ? 'decisions' : null;
  }
}
